import type { ProjectState } from "hooks/useProjects/useProjects.types";

export type RunAiHubPipelineFlags = {
  hasScriptBreakdown: boolean;
  hasCharacterBreakdown: boolean;
  hasCoverage: boolean;
  hasSchedule: boolean;
  hasBudget: boolean;
};

/**
 * Resolves which Run AI hub rows to show. When the API leaves all `project.features` pipeline flags
 * false (stale row / partial payload) but the project is clearly a licensed OTP with credits, infer
 * the intended bundle from wallet + tier so the hub is not an empty shell.
 */
export function resolveRunAiHubPipelineVisibility(
  project: ProjectState | null
): RunAiHubPipelineFlags {
  if (!project?.features) {
    return {
      hasScriptBreakdown: false,
      hasCharacterBreakdown: false,
      hasCoverage: false,
      hasSchedule: false,
      hasBudget: false,
    };
  }

  const f = project.features;
  let hasScriptBreakdown = Boolean(f.hasScriptBreakdown);
  let hasCharacterBreakdown = Boolean(f.hasCharacterBreakdown);
  let hasCoverage = Boolean(f.hasCoverage);
  let hasSchedule = Boolean(f.hasSchedule);
  let hasBudget = Boolean(f.hasBudget);

  const allOff =
    !hasScriptBreakdown && !hasCharacterBreakdown && !hasCoverage && !hasSchedule && !hasBudget;

  if (!allOff) {
    return {
      hasScriptBreakdown,
      hasCharacterBreakdown,
      hasCoverage,
      hasSchedule,
      hasBudget,
    };
  }

  const trial = Boolean(project.isTrial);
  const licensed =
    project.isLicensed !== false && project.permissions?.projectInfo?.isLicensed !== false;
  if (trial || !licensed) {
    return {
      hasScriptBreakdown,
      hasCharacterBreakdown,
      hasCoverage,
      hasSchedule,
      hasBudget,
    };
  }

  // A licensed subscription project gets the full bundle, same as Studio. Indie is sold as
  // including scheduling and budgeting, so defaulting it to the Writer-equivalent set here
  // contradicted what subscribers were told they were paying for.
  const tier = project.otpSeating?.licenseTier;
  if (tier === "writer") {
    return {
      hasScriptBreakdown: true,
      hasCharacterBreakdown: true,
      hasCoverage: true,
      hasSchedule: false,
      hasBudget: false,
    };
  }

  return {
    hasScriptBreakdown: true,
    hasCharacterBreakdown: true,
    hasCoverage: true,
    hasSchedule: true,
    hasBudget: true,
  };
}
